import { DesignClass, ObjectClass } from '@app/types';

/**
 * Make types available in JSDocs without importing them since this is not a TS file.
 *
 * @import {
 *   Design,
 *   DesignVariable
 * } from '../types';
 */

// TODO: can't use Constnt file because it refrences window object, which is not available in Web Worker
const SET_APPLICATION_VARIABLES_ESCAPED = "api\\.session\\(\\)\\.set\\((\"|')";
const GET_APPLICATION_VARIABLES_ESCAPED = "api\\.session\\(\\)\\.get\\((\"|')";
const SET_PROPERTIES_VARIABLES_ESCAPED = "api\\.application\\(\\)\\.properties\\(\\)\\.set\\((\"|')";
const GET_PROPERTIES_VARIABLES_ESCAPED = "api\\.application\\(\\)\\.properties\\(\\)\\.get\\((\"|')";
export default class VariablesUsedIn {
    static getVariablesUsedInMap(serialized){
        const variablesUsedInMap = {};
        const { components } = serialized;
        const { appProperties } = serialized;

        components.variables.forEach(v => {
            const variableUsedIn = VariablesUsedIn.getVariableUsedIn(v, serialized);
            variablesUsedInMap[v.id] = variablesUsedInMap[v.id] || [];
            variablesUsedInMap[v.id].push(variableUsedIn);
        });

        return variablesUsedInMap;
    }

    /**
     * @param {Design} serialized
     * @returns {DesignVariable[]}
     */
    static getUnusedVariables(serialized){
        const {components} = serialized;
        const variablesUsedInMap = VariablesUsedIn.getVariablesUsedInMap(serialized);
        const unusedVariables = [];

        Object.entries(variablesUsedInMap).forEach(ent => {
            if (ent[1][0].length === 0) {
                unusedVariables.push(components.variables.find(v => v.id === ent[0]));
            }
        });
    
        return unusedVariables;
    }

    static getLocalVariableUsedIn(vraName, parentId, serialized){
        // CM TODO: 

        // returns {mod, node, type = 'inLocalVariables'}
    }
    
    static getPrefix(config){
        let prefix = "app.";

        if(config.scope){
            if(config.scope === "conversation"){
                prefix = "conv."
            }
        }

        return prefix;
    }

    // UsedIn popup
    static getVariableUsedIn(v, serialized){
        
        const prefix = VariablesUsedIn.getPrefix(v.props.config);
        const vName = prefix + v.name;
        const scriptName = vName.split(".")[1];
        const usedInVariables = [];
        const {components, functions, globalIntents, appProperties} = serialized;
        const variableName = v.name;
        const setAppregex = new RegExp(SET_APPLICATION_VARIABLES_ESCAPED + variableName +"(\"|')" , "g");
        const getAppregex = new RegExp(GET_APPLICATION_VARIABLES_ESCAPED + variableName +"(\"|')" , "g");
        const setPropertiesRegex = new RegExp(SET_PROPERTIES_VARIABLES_ESCAPED + variableName +"(\"|')" , "g");
        const getPropertiesRegex = new RegExp(GET_PROPERTIES_VARIABLES_ESCAPED + variableName +"(\"|')" , "g");
        const nodesWithVariables = [
            DesignClass.module,
            ObjectClass.data,
            ObjectClass.custom,
            ObjectClass.exit
        ];

        // look in Reusable Functions
        functions.forEach(func => {
            let variableIsFound = false;
            func?.parameters?.forEach(param =>{
                if(param?.value === vName){
                    variableIsFound = true;
                }
            });
            if(func?.script.includes(scriptName)){
                variableIsFound = true;
            }
            if(variableIsFound){
                usedInVariables.push({
                    func,
                    type: 'inReusableFunction'
                });
            }
        });
        // look in modules
        components.modules.forEach(mod => {
            const nodes = mod.versions[1].flow.nodes;

            const filteredNodes = nodes.filter(node => {
                return nodesWithVariables.findIndex(nwv => nwv === node._class) !== -1;
            });

            filteredNodes.forEach(node => {
                // why can nodes have missing inputs/outputs?
                node.props.config['inputs'] = node.props.config['inputs'] || [];
                node.props.config['outputs'] = node.props.config['outputs'] || [];

                let variableIsFound = false;
                let isSetApp = false;
                let isGetApp = false;

                if(prefix === "app."){
                    variableIsFound = node.props.config['inputs'].concat(node.props.config['outputs']).findIndex(i => i.varName === vName) !== - 1;

                    // set these booleans if the variable is used on the function script
                    const scriptText = node.props.config.function?.script;
                    isSetApp = setAppregex.test(scriptText) || setPropertiesRegex.test(scriptText);
                    isGetApp = getAppregex.test(scriptText) || getPropertiesRegex.test(scriptText);

                    if (!variableIsFound) { // if it is not found in inputs or outputs, check the function script
                        if (scriptText) {
                            variableIsFound = isSetApp || isGetApp;
                        }

                        // Search specifically on Data Access Nodes
                        if (node._class === ObjectClass.data) {
                            const httpRequestTimeoutVal = node.props.config.httpRequestTimeout || null;
                            variableIsFound = httpRequestTimeoutVal.toString() === vName;
                        }
                    }
                }
                // TODO: this conditional is too long;
                // this is due to early versions of Channel End exit nodes not having the conversationVariables array on their exitComponent 
                if(prefix === "conv." && node.props.config.exitHandling === "CHANNEL_END" && node.props.config.exitComponent && node.props.config.exitComponent.conversationVariables){
                    variableIsFound = node.props.config.exitComponent.conversationVariables.findIndex(ci => ci.key === vName) !== -1; 
                }
                
                if(variableIsFound){
                    usedInVariables.push({
                        mod,
                        node,
                        isSetApp,
                        isGetApp,
                        type: 'inFlowNode'
                    });
                }
            });

            const links = mod.versions[1].flow.links;

            links.forEach(link => {
                // can links also have missing inputs?
                link.props.config.inputs = link.props.config.inputs || [];

                let variableIsFound = link.props.config.inputs.findIndex(i => i.varName === vName) !== - 1;
                
                if (variableIsFound) {
                    usedInVariables.push({
                        mod,
                        link,
                        type: 'inFlowLink'
                    });
                }
            });
        });

        // look in Application Object configs
        components.objects.forEach(obj => {
            const inputs = obj.props.config['inputs'] || []; 
            const outputs = obj.props.config['outputs'] || [];
            const promptText = (obj.props.config.text && obj.props.config.text['en-US']) ? obj.props.config.text['en-US'] : null;   
            const scriptText = (obj.props.config.function && obj.props.config.function['script']) ? obj.props.config.function['script'] : null;
            const funcArgs = (obj.props.config.function && obj.props.config.function['arguments']) ? obj.props.config.function['arguments'] : null;
            const dataAccessScript = obj.props.config.customScript ? obj.props.config.customScript : null;
            const isFoundInRefrenceFunction = (obj.props.config.function && obj.props.config.function['ref']) ? 
                // test that function can be found before looking for v.name in script 
                (functions.find(f => f.id === obj.props.config.function['ref'])) ?
                    functions.find(f => f.id === obj.props.config.function['ref']).script.match(setAppregex) !== null : false  
                || 
                // test that function can be found before looking for v.name in script
                (functions.find(f => f.id === obj.props.config.function['ref'])) ? 
                    functions.find(f => f.id === obj.props.config.function['ref']).script.match(getAppregex) !== null : false : false;
            
            const scriptProgress = obj.props.config.scriptProgress || null;
            const transferReason = obj.props.config.transferReason || null;
            const transferDestination = obj.props.config.transferDestination || null;
            const billing = obj.props.config.billing || null;
            const customTag = obj.props.config.customTag && obj.props.config.customTag['value'] || null;
            const promptReference = obj.props.config.promptReference || null;
            const moduleReference = obj.props.config.moduleReference || null;
            const analystClass = obj.props.config.analystClass || null;
            const externalAudioUrlVarName = obj.props.config.externalAudioUrlVarName || null;
            const exitComponentCustomHeaders = obj.props.config.exitComponent && obj.props.config.exitComponent.customHeaders || [];   
            const exitComponentDestination = obj.props.config.exitComponent && obj.props.config.exitComponent.destination || null;
            const sessionNodeDestination = obj.props.config['destination'] || null;
            const httpRequestTimeout = obj.props.config.httpRequestTimeout || null;
            const isVariableFound = [...inputs, ...outputs].findIndex(i => i.varName === vName) !== -1
                || promptText && promptText.indexOf(vName) !== -1
                || scriptText && scriptText.match(setAppregex) !== null
                || scriptText && scriptText.match(getAppregex) !== null
                || funcArgs && funcArgs.findIndex(args => args.varName === vName) !== -1
                || isFoundInRefrenceFunction
                || scriptProgress && scriptProgress.indexOf(v.name) !== -1
                || transferReason && transferReason.indexOf(v.name) !== -1
                || transferDestination && transferDestination.indexOf(v.name) !== -1
                || customTag && customTag.indexOf(v.name) !== -1
                || billing&&billing['value'] && billing['value'].indexOf(v.name) !== -1
                || promptReference && promptReference.indexOf(v.name) !== -1
                || moduleReference && moduleReference.indexOf(v.name) !== -1
                || analystClass && analystClass.indexOf(v.name) !== -1
                || exitComponentCustomHeaders && exitComponentCustomHeaders.findIndex(ch => ch['value'].indexOf(v.name) !== -1) !== -1
                || exitComponentDestination && exitComponentDestination === v.id
                || externalAudioUrlVarName && externalAudioUrlVarName === vName
                || dataAccessScript && dataAccessScript.match(setAppregex) !== null
                || dataAccessScript && dataAccessScript.match(getAppregex) !== null
                || sessionNodeDestination && sessionNodeDestination === vName
                || httpRequestTimeout && httpRequestTimeout.toString() === vName;
            const isSetApp = setAppregex.test(scriptText) || setPropertiesRegex.test(scriptText);
            const isGetApp = getAppregex.test(scriptText) || getPropertiesRegex.test(scriptText);

            if (isVariableFound) {
                usedInVariables.push({
                    obj,
                    isSetApp,
                    isGetApp,
                    type: 'inApplicationObjectConfig'
                });
            }
        });
// globalIntents module inputs
        globalIntents.forEach(gi => {
            if (gi.module && gi.moduleProperties.props.config.inputs && gi.moduleProperties.props.config.inputs.length > 0) {
                if (gi.moduleProperties.props.config.inputs.findIndex(input => input.id === v.id) !== -1) {
                    usedInVariables.push({
                        gi,
                        type: 'inGlobalIntent'
                    });
                }
            }
            if (gi.intent && gi.conditionProperties.props.config.inputs && gi.conditionProperties.props.config.inputs.length > 0) {
                if (gi.conditionProperties.props.config.inputs.findIndex(input => input.argumentName === vName) !== -1) {
                    usedInVariables.push({
                        gi,
                        type: 'inGlobalIntent'
                    });
                }
            }
        });
//variables in appProperties
        appProperties.forEach(appProp => {
            if (appProp.type === "variableBinding"&& appProp.variable === vName) {
                usedInVariables.push({
                    appProp,
                    type: 'inAppProperties'
                });
            }
        })
       

        return usedInVariables;
    }
}